/*
 * Tracking Target View Controller
 * Controller As: "targetsView"
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiRouterModule from "@uirouter/angularjs";
import uiRouterPreviousStateModule from "tbClient/services/PreviousStateService";
import uiBootstrapModule from "angular-ui-bootstrap";
import kendoDirectives from "../../shims/kendoDirectives";



import Assert from "tbAssert";
import DependencyListBuilder from "../../utility/DependencyListBuilder";

import TrackingTargetViewModuleConstants from "./Constants";

import TrackingTargetCandidaciesEditorController from "./TrackingTargetCandidaciesEditorController";
import TargetName from "./TargetName";
import MessageQueue from "tbClient/models/MessageQueue";
import EventFilter from "tbClient/models/EventFilter";
import { GlobalViewDefinition } from "tbClient/models/GlobalView";

import ElectionCandidateDataServiceModule from "../../services/ElectionCandidateDataService";
import TrackingTargetDataServiceModule from "../../services/TrackingTargetDataService";
import TrackingCalendarDataServiceModule from "../../services/TrackingCalendarDataService";
import UserInfoServiceModule from "../../services/UserInfoService";
import EventTitleGridTemplateBuilderModule from "../../services/EventTitleGridTemplateBuilder";
import StatusLabelComponentModule from "../../directives/StatusLabel";
import FormatTimeZoneNameFilterModule from "../../filters/FormatTimeZoneNameFilter";
import OfficeTypeLabelTypeFilterModule from "../../filters/OfficeTypeLabelTypeFilter";
import DateFormatFilterModule from "../../filters/MomentFormatDateFilter";
import CalendarGlobalViewServiceModule from "tbClient/services/CalendarGlobalViewService";

const viewPageTitleBase = "TrackerBase: Tracking Targets";
const viewPageRootElementId = "tbTargetsViewRoot";

const mAnchorScrollSvc = new WeakMap();
const mScopeSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mQSvc = new WeakMap();
const mFilterSvc = new WeakMap();

const mUIModalSvc = new WeakMap();
const mUIRouterStateSvc = new WeakMap();
const mUIRouterPreviousStateSvc = new WeakMap();

const mUserInfoSvc = new WeakMap();
const mTrackingTargetDataSvc = new WeakMap();
const mTrackingCalendarDataSvc = new WeakMap();
const mElectionCandidateDataSvc = new WeakMap();
const mGlobalViewSvc = new WeakMap();
const mEventTitleGridTemplateBuilder = new WeakMap();

const defaultMessageType = "info";
const errorMessageType = "danger";
const successMessageType = "success";
const defaultMessageTimeout = 2500;
const mStatusMessageQueue = new WeakMap();

const mLnTargets = new WeakMap();

const mShowSideDrawer = new WeakMap();
const mTargetActionMenuIsOpen = new WeakMap();

const mActiveTarget = new WeakMap();

const mTargetNameEditModel = new WeakMap();
const mTargetNameEditingMode = new WeakMap();
const mTargetCreateMode = new WeakMap();

const eventGridDateFormat = "M/D/YYYY, h:mm A"; //format string for momentjs

const mEventGridDataSource = new WeakMap();
const mEventGridOptions = new WeakMap();




class TrackingTargetsViewController {
    constructor($scope,
                $anchorScroll,
                $document,
                $timeout,
                $log,
                $q,
                $filter,
                $state,
                $transition$,
                $previousState,
                $uibModal,
                TrackingTargetData,
                TrackingCalendarData,
                ElectionCandidateData,
                UserInfoService,
                CalendarGlobalViewService,
                EventTitleGridTemplateBuilder)
    {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($anchorScroll != null, "Assertion Failure: Missing Anchor Scroll Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($filter != null, "Assertion Failure: Missing Filter Service Dependency");

        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");
        Assert.StrictAssert($transition$ != null, "Assertion Failure: Missing UI Router Transition Object Dependency");
        Assert.StrictAssert($previousState != null, "Assertion Failure: Missing UI Router Previous State Service Dependency");
        Assert.StrictAssert($uibModal != null, "Assertion Failure: Missing UIB Modal Service Dependency");

        Assert.StrictAssert(TrackingTargetData != null, "Assertion Failure: Missing Tracking Target Data Service Dependency");
        Assert.StrictAssert(TrackingCalendarData != null, "Assertion Failure: Missing Tracking Calendar Data Service Dependency");
        Assert.StrictAssert(ElectionCandidateData != null, "Assertion Failure: Missing Election Candidate Data Service Dependency");
        Assert.StrictAssert(UserInfoService != null, "Assertion Failure: Missing User Info Service Dependency");
        Assert.StrictAssert(CalendarGlobalViewService != null, "Assertion Failure: Missing Global View Service Dependency");

        Assert.StrictAssert(EventTitleGridTemplateBuilder != null, "Assertion Failure: Missing Event Title Grid Builder Service Dependency");

        mAnchorScrollSvc.set(this, $anchorScroll);
        mScopeSvc.set(this, $scope);
        mDocumentSvc.set(this, $document);
        mTimeoutSvc.set(this, $timeout);
        mLogSvc.set(this, $log);
        mQSvc.set(this, $q);
        mFilterSvc.set(this, $filter);

        mUIRouterStateSvc.set(this, $state);
        mUIRouterPreviousStateSvc.set(this, $previousState);
        mUIModalSvc.set(this, $uibModal);

        mUserInfoSvc.set(this, UserInfoService);
        mTrackingTargetDataSvc.set(this, TrackingTargetData);
        mTrackingCalendarDataSvc.set(this, TrackingCalendarData);
        mElectionCandidateDataSvc.set(this, ElectionCandidateData);
        mGlobalViewSvc.set(this, CalendarGlobalViewService);
        mEventTitleGridTemplateBuilder.set(this, EventTitleGridTemplateBuilder);

        mStatusMessageQueue.set(this, new MessageQueue(defaultMessageType, defaultMessageTimeout));

        mShowSideDrawer.set(this, false);
        mTargetActionMenuIsOpen.set(this, false);

        mActiveTarget.set(this, null);

        let eventGridDataSourceParams = {
            transport: {
                read: (options) => {
                    if (this.activeTarget == null) {
                        options.success({ totalItems: 0, serverData: []});
                        return;
                    }

                    mTrackingCalendarDataSvc.get(this).queryEvents(
                        options.data.page,
                        options.data.pageSize,
                        options.data.filter,
                        options.data["sort"]
                        )
                        .then(
                            (response) => {
                                options.success(response);
                            },
                            (response) => {
                                options.error(response);
                            }
                        );
                }
            },
            change: (e) => {
                angular.noop(e);
                return mTimeoutSvc.get(this)(
                    () => {
                        mAnchorScrollSvc.get(this)("targets-view-events");
                    },
                    0
                );
            },
            serverPaging: true,
            serverFiltering: true,
            serverSorting: true,
            sort: {
                field: "start",
                dir: "desc"
            },
            schema: {
                model: {
                    id: "trackingEventId",
                    fields: {
                        trackingEventId: {
                            from: "trackingEventId",
                            type: "number"
                        },
                        displayTitle: { from: "eventDisplayName" },
                        start: { from: "startDate", type: "date", parse: function(sd) {
                            let datedSd = new Date(sd.getTime());
                            return datedSd;
                        }},
                        end: { from: "endDate", type: "date" },
                        eventTimezone: { from: "eventTimezone" },
                        statusName: { from: "statusName" },
                        locationName: { from: "locationName" },
                        locationAddress: { from: "locationAddress" },
                        locationCity: { from: "locationCity" },
                        locationStateName: { from: "locationState.shortName" },
                        eventTypes: {
                            from: "eventTypes",
                            parse: (data) => data.map((et) => et.eventTypeName).join(", ")
                        }
                    }
                },
                data: (serverData) => serverData.eventData,
                total: "totalItems"
            },
            pageSize: 25
        };

        mEventGridDataSource.set(this, new kendo.data.DataSource(eventGridDataSourceParams));

        let eventTitleGridTemplate = mEventTitleGridTemplateBuilder.get(this).eventTitleTemplate;

        mEventGridOptions.set(this, {
            columns: [
                {
                    field: "displayTitle",
                    title: "Name",
                    headerAttributes: { "ng-non-bindable": true },
                    template: eventTitleGridTemplate,
                    sortable: true
                },
                {
                    field: "start",
                    title: "Start",
                    headerAttributes: { "ng-non-bindable": true },
                    // Due to an apparent bug in kendo ui, the "start" and "startDate" properties from the model are
                    // not present on the dataItem provided to the angular template.
                    // I believe that the dataItem only contains enumerable properties from the model, including
                    // any enumerable properties on the object that the model was created from. The source object here
                    // is a Tracking Event, so as a workaround, we use our moment format filter to display the
                    // TrackingEvent's eventStart property (which was created in the class in a way that makes it
                    // enumerable).
                    template: `{{dataItem.eventStart | tbMomentDate: '${eventGridDateFormat}'}}`,
                    sortable: true
                },
                {
                    field: "end",
                    title: "End",
                    headerAttributes: { "ng-non-bindable": true },
                    // See note on Start above, but substitute "end", "endDate" and "eventEnd" where relevant.
                    template: `{{dataItem.eventEnd | tbMomentDate : '${eventGridDateFormat}'}}`,
                    sortable: true
                },
                {
                    field: "eventTimezone",
                    title: "Time Zone",
                    headerAttributes: { "ng-non-bindable": true },
                    template: "{{dataItem.eventTimezone | tbFormatTZName }}",
                    sortable: false
                },
                {
                    field: "statusName",
                    title: "Status",
                    headerAttributes: { "ng-non-bindable": true },
                    sortable: true
                },
                {
                    field: "locationName",
                    title: "Location Name",
                    headerAttributes: { "ng-non-bindable": true },
                    sortable: false
                },
                {
                    field: "locationCity",
                    title: "City",
                    headerAttributes: { "ng-non-bindable": true },
                    sortable: false
                },
                {
                    field: "locationStateName",
                    title: "State",
                    headerAttributes: { "ng-non-bindable": true },
                    sortable: true
                },
                {
                    field: "eventTypes",
                    title: "Event Types",
                    headerAttributes: { "ng-non-bindable": true },
                    sortable: false
                }
            ],
            scrollable: false,
            sortable: {
                allowUnsort: false,
                mode: "single"
            },
            pageable: true,
            allowCopy: true
        });

        let lnLetters = [
            "A","B","C","D","E","F",
            "G","H","I","J","K","L",
            "M","N","O","P","Q","R",
            "S","T","U","V","W","X",
            "Y","Z"
        ];

        mLnTargets.set(this, lnLetters.map(
            (ln) => {
                return {
                    letter: ln,
                    active: false,
                    targets: []
                };
            }
        ));

        mTargetNameEditModel.set(this, null);
        mTargetNameEditingMode.set(this, false);
        mTargetCreateMode.set(this, false);

        mDocumentSvc.get(this).attr("title", viewPageTitleBase);

        $scope.$on("$destroy", () => {
            let targetsViewRootElement = angular.element(`#${viewPageRootElementId}`);
            kendo.destroy(targetsViewRootElement);
        });

        const currentStateParams = $transition$.params();
        let targetIdUrlParam = currentStateParams.targetId;
        if (targetIdUrlParam != null) {
            let targetIdParam = parseInt(targetIdUrlParam, 10);
            if (Number.isInteger(targetIdParam)) {
                targetIdParam = Math.abs(targetIdParam);
                this.loadTargetFromParam(targetIdParam);
            }
        }
    }

    $onInit() {
        const globalViewSvc = mGlobalViewSvc.get(this);
        mAnchorScrollSvc.get(this).yOffset = angular.element("#tbTargetsViewHeaderPanel");

        mScopeSvc.get(this).$watch(
            () => globalViewSvc.getCurrentView(),
            (newVal, oldVal) => {
                if (GlobalViewDefinition.WatchComparer(newVal, oldVal)) {
                    if (this.activeTarget != null) {
                        this._updateEventGridForActiveTarget();
                    }
                }
            });
    }

    _createEventGridFilterForId(targetId) {
        return new EventFilter({
            targetFilter: [targetId],
        });
    }

    get userInfo() {
        return mUserInfoSvc.get(this);
    }

    get messages() {
        return mStatusMessageQueue.get(this).messages;
    }

    get showSideDrawer() {
        return mShowSideDrawer.get(this);
    }

    get targetActionMenuIsOpen() {
        return mTargetActionMenuIsOpen.get(this);
    }

    set targetActionMenuIsOpen(updatedVal) {
        mTargetActionMenuIsOpen.set(this, updatedVal);
    }

    get activeTarget() {
        return mActiveTarget.get(this);
    }

    get lnTargets() {
        return mLnTargets.get(this);
    }

    get targetNameEditModel() {
        return mTargetNameEditModel.get(this);
    }

    get targetNameEditingMode() {
        return mTargetNameEditingMode.get(this);
    }

    get targetCreateMode() {
        return mTargetCreateMode.get(this);
    }

    get eventGridDataSource() {
        return mEventGridDataSource.get(this);
    }

    get eventGridOptions() {
        return mEventGridOptions.get(this);
    }

    closeMessage(msgIdx) {
        mStatusMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    toggleSidebar() {
        mShowSideDrawer.set(this, !this.showSideDrawer);
    }

    isActiveTarget(targetId) {
        if (this.activeTarget == null) {
            return { "active": false };
        }

        let targetIsActive = (this.activeTarget.trackingTargetId === targetId);
        return {
            "active": targetIsActive
        };
    }

    cycleTargetsMenu(tgt) {
        let menuLetter = tgt.lastName[0];

        let menuLnTarget = this.lnTargets.find((lnTarget) => (lnTarget.letter === menuLetter));

        if (angular.isDefined(menuLnTarget)) {
            menuLnTarget.active = false;
            this.toggleTargetsSubMenu(menuLnTarget);
        }
    }

    loadTargetFromParam(trackingTargetId) {

        this.setActiveTarget(trackingTargetId, false)
            .then(
                (tgtData) => {
                    this.cycleTargetsMenu(tgtData);
                },
                (errorMessage) => {
                    if (errorMessage != null) {
                        mStatusMessageQueue.get(this).publishMessage(
                            errorMessage,
                            MessageQueue.timeoutOverrideValue,
                            errorMessageType
                        );
                    }
                }
            );
    }

    closeOpenLnTargets() {
        this.lnTargets.forEach((ln) => {
            ln.active = false;
        });
    }

    toggleTargetsSubMenu(lnTarget, e) {
        if (e != null) {
            e.preventDefault();
        }

        if (lnTarget != null) {
            if (lnTarget.active) {
                lnTarget.active = false;
            }
            else {
                //this.closeOpenLnTargets();
                lnTarget.active = true;
                this.openTargetsSubMenu(lnTarget);
            }
        }
    }

    openTargetsSubMenu(lnTarget) {
        this.getTargetsWithLN(lnTarget.letter)
            .then(
                (results) => {
                    lnTarget.targets.length = 0;
                    Array.prototype.push.apply(lnTarget.targets, results);
                },
                (errorMessage) => {
                    mLogSvc.get(this).error("Error in Open Targets Sub Menu Method");
                    mLogSvc.get(this).error(errorMessage);
                }
            );
    }

    getTargetsWithLN(ln) {
        return mTrackingTargetDataSvc.get(this)
            .queryStartsWith({lastName: ln})
            .$promise
            .then(
                //TODO: Check if results.queryResults matches structures returned by error handler.
                (results) => results.queryResults,
                (response) => {
                    mLogSvc.get(this).error("Error in Get Targets With Last Name");
                    mLogSvc.get(this).error(response);
                    return { queryResults: [] };
                }
            );
    }

    setupActiveTargetAfterServerCall(result) {
        mActiveTarget.set(this, result.trackingTarget);

        // for (let teIdx = this.activeTarget.targetElections.length - 1; teIdx >= 0; teIdx--) {
        //     angular.extend(this.activeTarget.targetElections[teIdx], { shouldRemove: false });
        // }
        // Rewritten as:
        this.activeTarget.targetElections.forEach((targetElection) => {
            angular.extend(targetElection, { shouldRemove: false });
        });

        mDocumentSvc.get(this).attr("title", `TrackerBase: ${this.activeTarget.displayName}`);

        mShowSideDrawer.set(this, false);

        mTimeoutSvc.get(this)(() => {

            let resultTargetId = result.trackingTarget.trackingTargetId;
            mUIRouterPreviousStateSvc.get(this).forget(TrackingTargetViewModuleConstants.prevStateLabel);
            mUIRouterStateSvc.get(this).go(TrackingTargetViewModuleConstants.rootStateName, {
                targetId: resultTargetId
            });
        }, 50);

        mTimeoutSvc.get(this)(() => {
            mAnchorScrollSvc.get(this)("targets-view-top");
        }, 1000);

        this._updateEventGridForActiveTarget();

        return this.activeTarget;
    }

    _updateEventGridForActiveTarget() {
        const eventGridFilter = this._createEventGridFilterForId(mActiveTarget.get(this).trackingTargetId);
        const currentGlobalView = mGlobalViewSvc.get(this).getCurrentView();
        mEventGridDataSource.get(this).filter(eventGridFilter.dataSourceFilter(currentGlobalView.rule));
    }

    setActiveTarget(targetId, setLocation, e) {

        if (e != null) {
            e.preventDefault();
        }

        angular.noop(setLocation);

        let setActiveTargetPromise =
            mTrackingTargetDataSvc.get(this)
                .getDetailed({targetId: targetId})
                .$promise
                .then(
                    (result) => this.setupActiveTargetAfterServerCall(result),
                    (errorMessage) => {
                        mActiveTarget.set(this, null);
                        mStatusMessageQueue.get(this).publishMessage(errorMessage, MessageQueue.timeoutOverrideValue, errorMessageType);
                        return mQSvc.get(this).reject(errorMessage);
                    }
                );

        return setActiveTargetPromise;
    }

    startCreateTarget() {
        if (this.targetNameEditModel == null) {

            mTargetNameEditModel.set(this, new TargetName());
            mTargetCreateMode.set(this, true);
        }
        else {
            mStatusMessageQueue.get(this).publishMessage(
                "Cannot create target while editing or creating is in progress. Please cancel the current operation first.",
                MessageQueue.timeoutOverrideValue,
                errorMessageType
            );
        }
    }

    saveNewTarget() {
        if ((!this.targetCreateMode) || (this.targetNameEditModel == null)) {
            return;
        }

        let savingMessageId = mStatusMessageQueue.get(this).publishMessage("Saving...", MessageQueue.timeoutOverrideValue);

        mTrackingTargetDataSvc.get(this).create(
            {},
            {
                LastName: this.targetNameEditModel.lastName,
                FirstName: this.targetNameEditModel.firstName
            }
        )
        .$promise
        .then(
            (createdTrackingTargetData) => {
                let createdTrackingTarget = this.setupActiveTargetAfterServerCall(createdTrackingTargetData);
                this.closeOpenLnTargets();
                this.cycleTargetsMenu(createdTrackingTarget);

                this.cancelCreateTarget();

                mStatusMessageQueue.get(this).removeMessageById(savingMessageId);
                mStatusMessageQueue.get(this).publishMessage("Created Successfully.", defaultMessageTimeout, successMessageType);
            },
            (errorResponse) => {
                mStatusMessageQueue.get(this).removeMessageById(savingMessageId);
                mStatusMessageQueue.get(this).publishMessage(errorResponse, MessageQueue.timeoutOverrideValue, errorMessageType);
            }
        );
    }

    cancelCreateTarget() {
        this.cancelTargetNameEdit();
    }

    startTargetNameEdit() {
        if ((this.targetNameEditModel == null) && (!this.targetCreateMode)) {
            mTargetNameEditModel.set(this, new TargetName(
                this.activeTarget.lastName.slice(0),
                this.activeTarget.firstName.slice(0)
            ));

            mTargetNameEditingMode.set(this, true);
        }
        else {
            mStatusMessageQueue.get(this).publishMessage(
                "Cannot edit another existing target while editing or creating is in progress. Please cancel the current operation first.",
                MessageQueue.timeoutOverrideValue,
                errorMessageType
            );
        }
    }

    saveTargetNameEdit() {

        if (this.targetNameEditingMode !== true) {
            return;
        }
        
        const statusMessageQueue = mStatusMessageQueue.get(this);

        let savingMessageId = statusMessageQueue.publishMessage("Saving...", MessageQueue.timeoutOverrideValue);

        let currentTrackingTargetId = this.activeTarget.trackingTargetId;

        mTrackingTargetDataSvc.get(this).update(
            { targetId: currentTrackingTargetId },
            {
                LastName: this.targetNameEditModel.lastName,
                FirstName: this.targetNameEditModel.firstName
            }
            )
            .$promise
            .then(
                (updatedTrackingTargetData) => {
                    if (updatedTrackingTargetData == null) {
                        return mQSvc.get(this).reject("Missing Tracking Target Update Result Data");
                    }

                    let updatedTrackingTarget = updatedTrackingTargetData.trackingTarget;

                    this.activeTarget.firstName = updatedTrackingTarget.firstName;
                    this.activeTarget.lastName = updatedTrackingTarget.lastName;

                    this.closeOpenLnTargets();
                    this.cycleTargetsMenu(updatedTrackingTarget);

                    this.cancelTargetNameEdit();

                    statusMessageQueue.removeMessageById(savingMessageId);
                    statusMessageQueue.publishMessage("Tracking Target Updated.", defaultMessageTimeout, successMessageType);
                },
                (errorResponse) => {
                    statusMessageQueue.removeMessageById(savingMessageId);
                    statusMessageQueue.publishMessage(errorResponse, MessageQueue.timeoutOverrideValue, errorMessageType);
                }
            );
    }

    // Cancels both create mode and edit mode (prevents protential inconsistency).
    cancelTargetNameEdit() {
        mTargetNameEditingMode.set(this, false);
        mTargetCreateMode.set(this, false);
        mTargetNameEditModel.set(this, null);
    }

    canRemoveElections() {
        if (this.activeTarget != null) {
            let removalSelections = this.activeTarget.targetElections.filter(
                (el) => (el.shouldRemove === true)
            );

            return (removalSelections.length > 0);
        }

        return false;
    }

    removeSelectedElections() {
        if (this.activeTarget == null) {
            return;
        }

        if (!this.userInfo.hasManagementRights) {
            return;
        }

        let currentTargetId = this.activeTarget.trackingTargetId;

        let removeSelectedElectionPromises = [];

        let removalSelections = this.activeTarget.targetElections.filter((el) => (el.shouldRemove === true));

        removalSelections.forEach(
            (selectedElection) => {
                let removeSelectedElectionPromise =
                    mElectionCandidateDataSvc.get(this)
                        .removeCandidate(selectedElection.electionId, currentTargetId)
                        .then(
                            (updatedElectionData) => {
                                angular.noop(updatedElectionData);
                                return true;
                            },
                            (errorResponse) => mQSvc.get(this).reject(errorResponse)
                        );

                removeSelectedElectionPromises.push(removeSelectedElectionPromise);
            }
        );

        mQSvc.get(this).all(removeSelectedElectionPromises)
            .then(
                (updateResults) => {
                    this.setActiveTarget(currentTargetId, false);

                    if (updateResults.length === removalSelections.length) {
                        mStatusMessageQueue.get(this).publishMessage(
                            "Elections updated sucessfully.",
                            defaultMessageTimeout,
                            successMessageType
                        );
                    }
                    else {
                        mStatusMessageQueue.get(this).publishMessage(
                            "Some changes failed.",
                            defaultMessageTimeout,
                            defaultMessageType
                        );
                    }
                },
                (errorMessages) => {
                    errorMessages.forEach(
                        (errMsg) => {
                            mStatusMessageQueue.get(this).publishMessage(errMsg, defaultMessageTimeout * 2, errorMessageType);
                        }
                    );
                }
            );

    }

    openElections(e) {
        if (e != null) {
            e.preventDefault();
        }
        
        const statusMessageQueue = mStatusMessageQueue.get(this);

        if (!this.userInfo.hasManagementRights) {
            statusMessageQueue.publishMessage(
                "You do not have permission to change a target's candidacies.",
                MessageQueue.timeoutOverrideValue,
                errorMessageType
            );
            return;
        }

        let currentTrackingTarget = this.activeTarget;

        let electionCandidacyModalParams = {
            animation: true,
            template: TrackingTargetCandidaciesEditorController.template,
            controller: TrackingTargetCandidaciesEditorController,
            controllerAs: TrackingTargetCandidaciesEditorController.controllerAs,
            bindToController: true,
            backdropClass: "tbIn",
            windowClass: "tbIn",
            size: "lg",
            resolve: {
                currentTarget: () => currentTrackingTarget
            }
        };

        let candidacyModalInstance = mUIModalSvc.get(this).open(electionCandidacyModalParams);

        return candidacyModalInstance.result
            .then(
                (candidaciesToAdd) => {

                    let savingMessageId = statusMessageQueue.publishMessage(
                        "Saving...",
                        MessageQueue.timeoutOverrideValue
                    );

                    let electionCandidateOpsPromises = [];

                    let currentTargetElectionIds = new Set(
                        this.activeTarget.targetElections.map((el) => el.electionId)
                    );

                    let currentTargetId = this.activeTarget.trackingTargetId;

                    //TODO: Make Changing Candidacy Start/End Dates More User-Friendly
                    candidaciesToAdd.forEach(
                        (candidacyInfo) => {

                            if (currentTargetElectionIds.has(candidacyInfo.electionId)) {

                                let updateOpPromise =
                                    mElectionCandidateDataSvc.get(this)
                                        .updateCandidate(
                                            candidacyInfo.electionId,
                                            currentTargetId,
                                            candidacyInfo.candidacyStart,
                                            candidacyInfo.candidacyEnd
                                        )
                                        .then(
                                            (electionData) => {
                                                angular.noop(electionData);
                                                return true;
                                            },
                                            (errorResponse) => mQSvc.get(this).reject(errorResponse)
                                        );

                                electionCandidateOpsPromises.push(updateOpPromise);
                            }
                            else {

                                let addOpPromise =
                                    mElectionCandidateDataSvc.get(this)
                                        .addCandidate(
                                            candidacyInfo.electionId,
                                            currentTargetId,
                                            candidacyInfo.candidacyStart,
                                            candidacyInfo.candidacyEnd
                                        )
                                        .then(
                                            (electionData) => {
                                                angular.noop(electionData);
                                                return true;
                                            },
                                            (errorResponse) => mQSvc.get(this).reject(errorResponse)
                                        );

                                electionCandidateOpsPromises.push(addOpPromise);
                            }
                        }
                    );

                    return mQSvc.get(this).all(electionCandidateOpsPromises)
                        .then(
                            (opResults) => {
                                let opResultsOk = opResults.every((opResult) => (opResult === true));

                                if (opResultsOk === true) {
                                    statusMessageQueue.removeMessageById(savingMessageId);
                                    let initialSuccessMessageId =
                                        statusMessageQueue.publishMessage(
                                            "Success. Reloading tracking target data ...",
                                            null,
                                            successMessageType
                                        );

                                    let reloadPromise = this.setActiveTarget(currentTargetId, false);

                                    reloadPromise
                                        .then(
                                            () => {
                                                statusMessageQueue.removeMessageById(initialSuccessMessageId);
                                                statusMessageQueue.publishMessage(
                                                    "Saved Sucessfully.",
                                                    defaultMessageTimeout,
                                                    successMessageType
                                                );

                                                //COMPLETE SUCCESS POINT.
                                            },
                                            () => {
                                                // Error handling following target data reload.
                                                // As setActiveTarget publishes its errors to
                                                // the status message queue, we just need to
                                                // remove our reloading message.
                                                statusMessageQueue.removeMessageById(initialSuccessMessageId);
                                                mQSvc.get(this).reject({ cancelled: true, reason: "error during reload." });
                                            }
                                        );
                                }
                                else {
                                    return mQSvc.get(this).reject({ cancelled: false, reason: "Unknown error processing operation results."});
                                }

                            },
                            (errorMessages) => {
                                // Error handling for those candidate save operations that
                                // resolve as rejected.
                                let consolidatedErrorMessage = errorMessages.join("\n");
                                statusMessageQueue.removeMessageById(savingMessageId);
                                statusMessageQueue.publishMessage(
                                    `One or more operations failed. Please provided the following data and report the bug: \n${consolidatedErrorMessage}`,
                                    MessageQueue.timeoutOverrideValue,
                                    errorMessageType
                                );
                            }
                        );

                },
                (errorResult) => {
                    // Error handling for when the user cancels out of the dialog
                    // or when the dialog closes unexpectedly.
                    if (errorResult.cancelled !== true) { //undefined should not be handled as a cancel.

                        let errorMessage = null;
                        if ((errorResult.reason != null) &&
                            (angular.isString(errorResult.reason)))
                        {
                            errorMessage = `Candidate dialog closed due to an error: ${errorResult.reason}`;
                        }
                        else {
                            errorMessage = "Candidate dialog closed due to an unknown error.";
                        }

                        statusMessageQueue.publishMessage(errorMessage, MessageQueue.timeoutOverrideValue, errorMessageType);
                    }
                }
            );
    }
}

TrackingTargetsViewController.$inject = [
    "$scope",
    "$anchorScroll",
    "$document",
    "$timeout",
    "$log",
    "$q",
    "$filter",
    "$state",
    "$transition$",
    "$previousState",
    "$uibModal",
    "TrackingTargetData",
    "TrackingCalendarData",
    "ElectionCandidateData",
    "UserInfoService",
    "CalendarGlobalViewService",
    "EventTitleGridTemplateBuilder"
];

TrackingTargetsViewController.dependencies = DependencyListBuilder([
    TrackingTargetCandidaciesEditorController.dependencies,
    [
        ngSanitize,
        ngAnimate,
        ngAria,
        uiRouterModule,
        uiRouterPreviousStateModule,
        uiBootstrapModule,
        kendoDirectives,
        ElectionCandidateDataServiceModule,
        TrackingTargetDataServiceModule,
        TrackingCalendarDataServiceModule,
        UserInfoServiceModule,
        CalendarGlobalViewServiceModule,
        EventTitleGridTemplateBuilderModule,
        StatusLabelComponentModule,
        FormatTimeZoneNameFilterModule,
        OfficeTypeLabelTypeFilterModule,
        DateFormatFilterModule
    ]
]);

TrackingTargetsViewController.template = require("./TrackingTargetsView.tpl.html");

TrackingTargetsViewController.controllerAs = "targetsView";

export default TrackingTargetsViewController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-targets-view/TrackingTargetsViewController.js
// module id = qnnz
// module chunks = 1